<template>
    <div class="manifest-product-behavior">
        <b-row>
            <b-col xl="5" class="mb-2 mb-xl-0">
                <section class="manifest-product-behavior__section h-100">
                    <div class="d-flex align-items-start justify-content-between mb-2">
                        <div>
                            <h5 class="mb-25">Import driver</h5>
                            <div class="text-muted">
                                Select the domain driver that will interpret canonical records.
                            </div>
                        </div>
                        <b-badge v-if="selectedDriver" variant="light-success">
                            v{{ selectedDriver.version }}
                        </b-badge>
                    </div>

                    <b-form-group label="Driver" label-for="manifest-product-driver">
                        <v-select
                            id="manifest-product-driver"
                            :value="selectedDriver"
                            :options="availableDrivers"
                            :clearable="false"
                            :disabled="disabled"
                            label="title"
                            placeholder="Choose an import driver"
                            @input="updateDriver"
                        >
                            <template #option="driver">
                                <div class="d-flex align-items-center justify-content-between">
                                    <span>{{ driver.title }}</span>
                                    <b-badge variant="light-secondary">v{{ driver.version }}</b-badge>
                                </div>
                                <small class="text-muted">{{ driver.key }}</small>
                            </template>
                            <template #selected-option="driver">
                                {{ driver.title }} · v{{ driver.version }}
                            </template>
                        </v-select>
                    </b-form-group>

                    <div v-if="selectedDriver" class="manifest-product-behavior__driver-info">
                        <div class="font-weight-bold mb-25">{{ selectedDriver.title }}</div>
                        <div class="text-muted mb-50">{{ selectedDriver.description }}</div>
                        <code>{{ selectedDriver.key }}@{{ selectedDriver.version }}</code>
                    </div>
                </section>
            </b-col>

            <b-col xl="7">
                <section class="manifest-product-behavior__section">
                    <template v-if="hasStructureConfiguration">
                        <div class="mb-2">
                            <h5 class="mb-25">Structure strategy</h5>
                            <div class="text-muted">
                                Choose how {{ domainTitle }} relationships are derived from source rows.
                            </div>
                        </div>

                        <div class="manifest-product-behavior__strategies">
                            <button
                                v-for="strategy in strategyOptions"
                                :key="strategy.key"
                                type="button"
                                class="manifest-product-behavior__strategy"
                                :class="{'manifest-product-behavior__strategy--selected': value.strategy === strategy.key}"
                                :disabled="disabled"
                                @click="updateStrategy(strategy.key)"
                            >
                                <span class="manifest-product-behavior__strategy-marker">
                                    <feather-icon
                                        :icon="value.strategy === strategy.key ? 'CheckIcon' : 'LayersIcon'"
                                        size="18"
                                    />
                                </span>
                                <span>
                                    <span class="manifest-product-behavior__strategy-title">
                                        {{ strategy.title }}
                                    </span>
                                    <code>{{ strategy.key }}</code>
                                </span>
                            </button>
                        </div>
                    </template>

                    <div
                        v-if="selectedDriver"
                        class="manifest-product-behavior__identity"
                        :class="{'manifest-product-behavior__identity--first': !hasStructureConfiguration}"
                    >
                        <div class="mb-1">
                            <h5 class="mb-25">Within-import identity</h5>
                            <div class="text-muted">
                                Choose how source rows are grouped into logical {{ domainTitle }} records in this file.
                            </div>
                        </div>

                        <b-form-group
                            v-if="hasIdentityPolicyChoice"
                            label="Grouping policy"
                            label-for="manifest-product-identity-policy"
                        >
                            <v-select
                                id="manifest-product-identity-policy"
                                :value="selectedIdentityPolicy"
                                :options="identityPolicyOptions"
                                :clearable="false"
                                :disabled="disabled"
                                label="title"
                                placeholder="Choose a grouping policy"
                                @input="updateIdentityPolicy"
                            >
                                <template #option="policy">
                                    <div>{{ policy.title }}</div>
                                    <small class="text-muted">{{ policy.description }}</small>
                                </template>
                                <template #selected-option="policy">
                                    {{ policy.title }}
                                </template>
                            </v-select>
                        </b-form-group>

                        <div
                            v-if="selectedIdentityPolicy && selectedIdentityPolicy.description"
                            class="manifest-product-behavior__policy-description"
                        >
                            {{ selectedIdentityPolicy.description }}
                        </div>

                        <b-form-group
                            v-if="hasIdentityFieldChoice"
                            label="Identity field"
                            label-for="manifest-product-identity"
                        >
                            <v-select
                                id="manifest-product-identity"
                                :value="selectedIdentityField"
                                :options="identityFieldOptions"
                                :clearable="false"
                                :disabled="disabled"
                                label="title"
                                placeholder="Choose a canonical identity field"
                                @input="updateIdentityField"
                            >
                                <template #option="field">
                                    <div>{{ field.title }}</div>
                                    <small class="text-muted">{{ field.key }}</small>
                                </template>
                                <template #selected-option="field">
                                    {{ field.title }}
                                </template>
                            </v-select>
                        </b-form-group>

                        <div
                            v-else-if="!hasIdentityPolicyChoice && selectedIdentityPolicy"
                            class="manifest-product-behavior__fixed-policy mb-1"
                        >
                            <div>
                                <div class="font-weight-bold">Fixed identity policy</div>
                                <small class="text-muted">
                                    This driver defines its identity policy in the configuration schema.
                                </small>
                            </div>
                            <b-badge variant="light-primary">
                                {{ selectedIdentityPolicy.title }}
                            </b-badge>
                        </div>

                        <b-form-checkbox
                            v-if="hasCaseSensitiveArgument"
                            :checked="value.caseSensitive"
                            :disabled="disabled || caseSensitiveIsFixed"
                            @input="updateCaseSensitive"
                        >
                            Identity matching is case-sensitive
                        </b-form-checkbox>
                    </div>

                    <div
                        v-if="existingPolicyProperty"
                        class="manifest-product-behavior__existing"
                    >
                        <div class="mb-1">
                            <h5 class="mb-25">Existing {{ domainTitle }} policy</h5>
                            <div class="text-muted">
                                Choose how imported records are handled when persisted {{ domainTitle }} records may already exist.
                            </div>
                        </div>

                        <b-form-group
                            v-if="hasExistingPolicyChoice"
                            label="Persisted record policy"
                            label-for="manifest-product-existing-policy"
                        >
                            <v-select
                                id="manifest-product-existing-policy"
                                :value="selectedExistingPolicy"
                                :options="existingPolicyOptions"
                                :clearable="false"
                                :disabled="disabled"
                                label="title"
                                placeholder="Choose an existing record policy"
                                @input="updateExistingPolicy"
                            >
                                <template #option="policy">
                                    <div>{{ policy.title }}</div>
                                    <small class="text-muted">{{ policy.description }}</small>
                                </template>
                                <template #selected-option="policy">
                                    {{ policy.title }}
                                </template>
                            </v-select>
                        </b-form-group>

                        <div
                            v-if="selectedExistingPolicy && selectedExistingPolicy.description"
                            class="manifest-product-behavior__policy-description"
                        >
                            {{ selectedExistingPolicy.description }}
                        </div>

                        <div
                            v-if="!hasExistingPolicyChoice && selectedExistingPolicy"
                            class="manifest-product-behavior__fixed-policy mb-1"
                        >
                            <div>
                                <div class="font-weight-bold">Fixed existing-record policy</div>
                                <small class="text-muted">Provided by the selected driver schema</small>
                            </div>
                            <b-badge variant="light-primary">
                                {{ selectedExistingPolicy.title }}
                            </b-badge>
                        </div>

                        <b-form-group
                            v-if="hasExistingIdentityFieldChoice"
                            label="Identifier field"
                            label-for="manifest-product-existing-identity"
                        >
                            <v-select
                                id="manifest-product-existing-identity"
                                :value="selectedExistingIdentityField"
                                :options="existingIdentityFieldOptions"
                                :clearable="false"
                                :disabled="disabled"
                                label="title"
                                placeholder="Choose an identifier field"
                                @input="updateExistingIdentityField"
                            >
                                <template #option="field">
                                    <div>{{ field.title }}</div>
                                    <small class="text-muted">{{ field.key }}</small>
                                </template>
                                <template #selected-option="field">
                                    {{ field.title }}
                                </template>
                            </v-select>
                        </b-form-group>

                        <b-form-group
                            v-else-if="hasExistingIdentityFieldsChoice"
                            label="Identifier fields"
                            label-for="manifest-product-existing-identities"
                        >
                            <v-select
                                id="manifest-product-existing-identities"
                                :value="selectedExistingIdentityFields"
                                :options="existingIdentityFieldsOptions"
                                :clearable="true"
                                :close-on-select="false"
                                :disabled="disabled"
                                multiple
                                label="title"
                                placeholder="Choose one or more identifier fields"
                                @input="updateExistingIdentityFields"
                            >
                                <template #option="field">
                                    <div>{{ field.title }}</div>
                                    <small class="text-muted">{{ field.key }}</small>
                                </template>
                                <template #selected-option="field">
                                    {{ field.title }}
                                </template>
                            </v-select>
                            <small class="text-muted d-block mt-50">
                                {{ existingIdentityFieldsHint }}
                                Every selected canonical field must be mapped.
                            </small>
                        </b-form-group>
                    </div>
                </section>
            </b-col>
        </b-row>

        <b-alert v-if="value.loadingRequirements" class="mt-2 mb-0" variant="info" show>
            <div class="alert-body d-flex align-items-center">
                <b-spinner small class="mr-75" />
                Resolving mapping requirements for this configuration…
            </div>
        </b-alert>

        <b-alert v-else-if="value.requirementsError" class="mt-2 mb-0" variant="danger" show>
            <div class="alert-body d-flex align-items-center justify-content-between">
                <span>Mapping requirements could not be resolved.</span>
                <b-button size="sm" variant="outline-danger" @click="$emit('retry-requirements')">
                    Retry
                </b-button>
            </div>
        </b-alert>

        <b-alert v-else-if="value.requirementsLoaded" class="mt-2 mb-0" variant="success" show>
            <div class="alert-body">
                {{ value.requirements.length }} mapping requirements resolved. Continue to Field mapping.
            </div>
        </b-alert>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
    components: {
        BAlert,
        BBadge,
        BButton,
        BCol,
        BFormCheckbox,
        BFormGroup,
        BRow,
        BSpinner,
        vSelect,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        drivers: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        availableDrivers() {
            return this.drivers.filter(driver => driver.status === 'active')
        },
        selectedDriver() {
            return this.drivers.find(driver => (
                driver.key === this.value.driver
                && driver.version === this.value.driverVersion
            )) || null
        },
        domainTitle() {
            return this.selectedDriver ? this.humanizeKey(this.selectedDriver.key) : 'record'
        },
        structureSchema() {
            return this.schemaProperty(
                this.selectedDriver ? this.selectedDriver.configurationSchema : null,
                'structure'
            )
        },
        hasStructureConfiguration() {
            return Object.keys(this.structureSchema).length > 0
        },
        strategyOptions() {
            const strategySchema = this.schemaProperty(this.structureSchema, 'strategy')
            const values = Array.isArray(strategySchema.enum)
                ? strategySchema.enum
                : strategySchema.const ? [strategySchema.const] : []

            return values.map(key => ({key, title: this.humanizeKey(key)}))
        },
        identityPolicyContainerSchema() {
            const identityPolicies = this.schemaProperty(
                this.selectedDriver ? this.selectedDriver.configurationSchema : null,
                'identityPolicies'
            )

            return this.schemaProperty(identityPolicies, 'withinImport')
        },
        identityPolicyOptions() {
            return this.policyOptions(this.identityPolicyContainerSchema, 'key')
        },
        hasIdentityPolicyChoice() {
            return this.identityPolicyOptions.length > 1
        },
        selectedIdentityPolicyKey() {
            const configured = this.value.configuration
                && this.value.configuration.identityPolicies
                && this.value.configuration.identityPolicies.withinImport

            return this.value.identityPolicy
                || (configured ? configured.key : null)
                || (this.identityPolicyOptions.length === 1
                    ? this.identityPolicyOptions[0].key
                    : null)
        },
        selectedIdentityPolicy() {
            return this.identityPolicyOptions.find(policy => (
                policy.key === this.selectedIdentityPolicyKey
            )) || null
        },
        identityPolicySchema() {
            return this.selectedIdentityPolicy ? this.selectedIdentityPolicy.schema : {}
        },
        identityArgumentsSchema() {
            return this.schemaProperty(this.identityPolicySchema, 'arguments')
        },
        identityFieldSchema() {
            return this.schemaProperty(this.identityArgumentsSchema, 'field')
        },
        identityFieldOptions() {
            const keys = Array.isArray(this.identityFieldSchema.enum)
                ? this.identityFieldSchema.enum
                : []
            const fields = this.selectedDriver && Array.isArray(this.selectedDriver.canonicalFields)
                ? this.selectedDriver.canonicalFields
                : []

            return keys.map(key => fields.find(field => field.key === key) || {
                key,
                title: this.humanizeKey(key),
                description: key,
                valueType: 'mixed',
            })
        },
        hasIdentityFieldChoice() {
            return Object.keys(this.identityFieldSchema).length > 0
        },
        selectedIdentityField() {
            const configured = this.value.configuration
                && this.value.configuration.identityPolicies
                && this.value.configuration.identityPolicies.withinImport
            const configuredField = configured && configured.arguments
                ? configured.arguments.field
                : null
            const key = this.value.identityField || configuredField

            return this.identityFieldOptions.find(field => field.key === key) || null
        },
        caseSensitiveSchema() {
            return this.schemaProperty(this.identityArgumentsSchema, 'caseSensitive')
        },
        hasCaseSensitiveArgument() {
            return Object.keys(this.caseSensitiveSchema).length > 0
        },
        caseSensitiveIsFixed() {
            return Object.prototype.hasOwnProperty.call(this.caseSensitiveSchema, 'const')
        },
        existingPolicyProperty() {
            const schema = this.selectedDriver ? this.selectedDriver.configurationSchema : null
            const properties = schema && schema.properties ? schema.properties : {}
            return Object.keys(properties).find(property => (
                property.startsWith('existing') && property.endsWith('Policy')
            )) || null
        },
        existingPolicyContainerSchema() {
            return this.existingPolicyProperty
                ? this.schemaProperty(this.selectedDriver.configurationSchema, this.existingPolicyProperty)
                : {}
        },
        existingPolicyOptions() {
            return this.policyOptions(this.existingPolicyContainerSchema, 'key')
        },
        hasExistingPolicyChoice() {
            return this.existingPolicyOptions.length > 1
        },
        selectedExistingPolicyKey() {
            const configured = this.value.configuration && this.existingPolicyProperty
                ? this.value.configuration[this.existingPolicyProperty]
                : null

            return this.value.existingPolicy
                || (configured ? configured.key : null)
                || (this.existingPolicyOptions.length === 1
                    ? this.existingPolicyOptions[0].key
                    : null)
        },
        selectedExistingPolicy() {
            return this.existingPolicyOptions.find(policy => (
                policy.key === this.selectedExistingPolicyKey
            )) || null
        },
        existingPolicySchema() {
            return this.selectedExistingPolicy ? this.selectedExistingPolicy.schema : {}
        },
        existingArgumentsSchema() {
            return this.schemaProperty(this.existingPolicySchema, 'arguments')
        },
        existingIdentityFieldSchema() {
            return this.schemaProperty(this.existingArgumentsSchema, 'field')
        },
        existingIdentityFieldOptions() {
            const keys = Array.isArray(this.existingIdentityFieldSchema.enum)
                ? this.existingIdentityFieldSchema.enum
                : []
            const fields = this.selectedDriver && Array.isArray(this.selectedDriver.canonicalFields)
                ? this.selectedDriver.canonicalFields
                : []

            return keys.map(key => fields.find(field => field.key === key) || {
                key,
                title: this.humanizeKey(key),
                description: key,
                valueType: 'mixed',
            })
        },
        hasExistingIdentityFieldChoice() {
            return Object.keys(this.existingIdentityFieldSchema).length > 0
        },
        selectedExistingIdentityField() {
            const configured = this.value.configuration && this.existingPolicyProperty
                ? this.value.configuration[this.existingPolicyProperty]
                : null
            const configuredField = configured && configured.arguments
                ? configured.arguments.field
                : null
            const key = this.value.existingIdentityField || configuredField

            return this.existingIdentityFieldOptions.find(field => (
                field.key === key
            )) || null
        },
        existingIdentityFieldsSchema() {
            return this.schemaProperty(this.existingArgumentsSchema, 'fields')
        },
        existingIdentityFieldsOptions() {
            const items = this.existingIdentityFieldsSchema.items || {}
            const keys = Array.isArray(items.enum) ? items.enum : []
            const fields = this.selectedDriver && Array.isArray(this.selectedDriver.canonicalFields)
                ? this.selectedDriver.canonicalFields
                : []

            return keys.map(key => fields.find(field => field.key === key) || {
                key,
                title: this.humanizeKey(key),
                description: key,
                valueType: 'mixed',
            })
        },
        hasExistingIdentityFieldsChoice() {
            return this.existingIdentityFieldsSchema.type === 'array'
        },
        existingIdentityFieldsMinimum() {
            return Number.isInteger(this.existingIdentityFieldsSchema.minItems)
                ? this.existingIdentityFieldsSchema.minItems
                : 0
        },
        existingIdentityFieldsHint() {
            if (this.existingIdentityFieldsMinimum === 1) {
                return 'Select one or more identifier fields.'
            }
            if (this.existingIdentityFieldsMinimum > 1) {
                return `Select at least ${this.existingIdentityFieldsMinimum} identifier fields.`
            }

            return 'Select identifier fields used for persisted lookup.'
        },
        selectedExistingIdentityFields() {
            const configured = this.value.configuration && this.existingPolicyProperty
                ? this.value.configuration[this.existingPolicyProperty]
                : null
            const configuredFields = configured
                && configured.arguments
                && Array.isArray(configured.arguments.fields)
                ? configured.arguments.fields
                : []
            const keys = Array.isArray(this.value.existingIdentityFields)
                && this.value.existingIdentityFields.length
                ? this.value.existingIdentityFields
                : configuredFields

            return keys.map(key => (
                this.existingIdentityFieldsOptions.find(field => field.key === key) || {
                    key,
                    title: this.humanizeKey(key),
                    description: key,
                    valueType: 'mixed',
                }
            ))
        },
    },
    watch: {
        availableDrivers: {
            immediate: true,
            handler(drivers) {
                this.selectOnlyDriver(drivers)
            },
        },
        disabled(disabled) {
            if (!disabled) {
                this.selectOnlyDriver(this.availableDrivers)
            }
        },
    },
    methods: {
        selectOnlyDriver(drivers) {
            if (this.disabled || this.selectedDriver || drivers.length !== 1) {
                return
            }

            this.updateDriver(drivers[0])
        },
        updateDriver(driver) {
            const configurationSchema = driver ? driver.configurationSchema : null
            const identityPolicies = this.schemaProperty(configurationSchema, 'identityPolicies')
            const withinImport = this.schemaProperty(identityPolicies, 'withinImport')
            const identityOptions = this.policyOptions(withinImport, 'key')
            const identityPolicy = identityOptions.length === 1 ? identityOptions[0] : null
            const properties = configurationSchema && configurationSchema.properties
                ? configurationSchema.properties
                : {}
            const existingPolicyProperty = Object.keys(properties).find(property => (
                property.startsWith('existing') && property.endsWith('Policy')
            )) || null
            const existingPolicySchema = existingPolicyProperty
                ? this.schemaProperty(configurationSchema, existingPolicyProperty)
                : {}
            const existingOptions = this.policyOptions(existingPolicySchema, 'key')
            const existingPolicy = existingOptions.length === 1 ? existingOptions[0] : null
            this.emitState({
                driver: driver ? driver.key : null,
                driverVersion: driver ? driver.version : null,
                strategy: null,
                identityPolicy: identityPolicy ? identityPolicy.key : null,
                identityField: null,
                caseSensitive: identityPolicy
                    ? this.caseSensitiveDefault(identityPolicy.schema)
                    : true,
                existingPolicy: existingPolicy ? existingPolicy.key : null,
                existingIdentityField: null,
                existingIdentityFields: [],
            })
        },
        updateStrategy(strategy) {
            this.emitState({strategy})
        },
        updateIdentityPolicy(policy) {
            this.emitState({
                identityPolicy: policy ? policy.key : null,
                identityField: null,
                caseSensitive: policy ? this.caseSensitiveDefault(policy.schema) : true,
            })
        },
        updateIdentityField(field) {
            this.emitState({identityField: field ? field.key : null})
        },
        updateCaseSensitive(caseSensitive) {
            this.emitState({caseSensitive})
        },
        updateExistingPolicy(policy) {
            this.emitState({
                existingPolicy: policy ? policy.key : null,
                existingIdentityField: null,
                existingIdentityFields: [],
            })
        },
        updateExistingIdentityField(field) {
            this.emitState({existingIdentityField: field ? field.key : null})
        },
        updateExistingIdentityFields(fields) {
            const keys = Array.isArray(fields) ? fields.map(field => field.key) : []
            this.emitState({existingIdentityFields: [...new Set(keys)]})
        },
        emitState(patch) {
            const state = {
                ...this.value,
                ...patch,
                requirements: [],
                requirementsLoaded: false,
                requirementsError: false,
            }
            state.configuration = this.buildConfiguration(state)
            this.$emit('input', state)
        },
        buildConfiguration(state) {
            const driver = this.drivers.find(item => (
                item.key === state.driver && item.version === state.driverVersion
            ))
            if (!driver) {
                return null
            }

            const configurationSchema = driver.configurationSchema
            const structureSchema = this.schemaProperty(configurationSchema, 'structure')
            const identityPoliciesSchema = this.schemaProperty(configurationSchema, 'identityPolicies')
            const withinImportContainer = this.schemaProperty(identityPoliciesSchema, 'withinImport')
            const identityOptions = this.policyOptions(withinImportContainer, 'key')
            const identityPolicyKey = state.identityPolicy
                || (identityOptions.length === 1 ? identityOptions[0].key : null)
            const identityOption = identityOptions.find(option => option.key === identityPolicyKey)
            if (!identityOption) {
                return null
            }
            const withinImportSchema = identityOption.schema
            const identityArgumentsSchema = this.schemaProperty(withinImportSchema, 'arguments')
            const identityArguments = {}
            const configuration = {}

            if (Object.keys(structureSchema).length > 0) {
                if (!state.strategy) {
                    return null
                }
                configuration.structure = this.policyConfiguration(
                    structureSchema,
                    'strategy',
                    state.strategy
                )
            }

            const identityFieldSchema = this.schemaProperty(identityArgumentsSchema, 'field')
            if (Object.keys(identityFieldSchema).length > 0) {
                if (!state.identityField) {
                    return null
                }
                identityArguments.field = state.identityField
            }

            const caseSensitiveSchema = this.schemaProperty(identityArgumentsSchema, 'caseSensitive')
            if (Object.keys(caseSensitiveSchema).length > 0) {
                identityArguments.caseSensitive = Object.prototype.hasOwnProperty.call(
                    caseSensitiveSchema,
                    'const'
                )
                    ? caseSensitiveSchema.const
                    : state.caseSensitive
            }

            configuration.identityPolicies = {
                withinImport: this.policyConfiguration(
                    withinImportSchema,
                    'key',
                    this.constantValue(withinImportSchema, 'key'),
                    identityArguments
                ),
            }

            const existingPolicyProperty = Object.keys(configurationSchema.properties || {}).find(
                property => property.startsWith('existing') && property.endsWith('Policy')
            )
            if (existingPolicyProperty) {
                const existingPolicyContainer = this.schemaProperty(
                    configurationSchema,
                    existingPolicyProperty
                )
                const existingOptions = this.policyOptions(existingPolicyContainer, 'key')
                const existingPolicyKey = state.existingPolicy
                    || (existingOptions.length === 1 ? existingOptions[0].key : null)
                const existingOption = existingOptions.find(option => (
                    option.key === existingPolicyKey
                ))
                if (!existingOption) {
                    return null
                }
                const existingPolicySchema = existingOption.schema
                const existingArgumentsSchema = this.schemaProperty(
                    existingPolicySchema,
                    'arguments'
                )
                const existingArguments = {}
                const existingFieldSchema = this.schemaProperty(existingArgumentsSchema, 'field')
                if (Object.keys(existingFieldSchema).length > 0) {
                    if (!state.existingIdentityField) {
                        return null
                    }
                    existingArguments.field = state.existingIdentityField
                }
                const existingFieldsSchema = this.schemaProperty(
                    existingArgumentsSchema,
                    'fields'
                )
                if (existingFieldsSchema.type === 'array') {
                    const fields = Array.isArray(state.existingIdentityFields)
                        ? state.existingIdentityFields
                        : []
                    const minimum = Number.isInteger(existingFieldsSchema.minItems)
                        ? existingFieldsSchema.minItems
                        : 0
                    const uniqueFields = [...new Set(fields)]
                    const itemSchema = existingFieldsSchema.items || {}
                    const allowedFields = Array.isArray(itemSchema.enum) ? itemSchema.enum : []
                    if (uniqueFields.length < minimum
                        || (existingFieldsSchema.uniqueItems && uniqueFields.length !== fields.length)
                        || (allowedFields.length && uniqueFields.some(field => !allowedFields.includes(field)))) {
                        return null
                    }
                    existingArguments.fields = uniqueFields
                }
                configuration[existingPolicyProperty] = this.policyConfiguration(
                    existingPolicySchema,
                    'key',
                    existingPolicyKey,
                    existingArguments
                )
            }

            return configuration
        },
        policyConfiguration(schema, identityProperty, identity, argumentsValue = {}) {
            return {
                [identityProperty]: identity,
                version: this.constantValue(schema, 'version'),
                arguments: argumentsValue,
            }
        },
        constantValue(schema, property) {
            const propertySchema = this.schemaProperty(schema, property)
            if (Object.prototype.hasOwnProperty.call(propertySchema, 'const')) {
                return propertySchema.const
            }

            return Array.isArray(propertySchema.enum) && propertySchema.enum.length === 1
                ? propertySchema.enum[0]
                : null
        },
        schemaProperty(schema, property) {
            return schema && schema.properties && schema.properties[property]
                ? schema.properties[property]
                : {}
        },
        policyOptions(schema, identityProperty) {
            const branches = schema && Array.isArray(schema.oneOf) && schema.oneOf.length
                ? schema.oneOf
                : Object.keys(schema || {}).length ? [schema] : []

            return branches.map(branch => {
                const key = this.constantValue(branch, identityProperty)
                return {
                    key,
                    title: branch.title || this.humanizeKey(key),
                    description: branch.description || '',
                    schema: branch,
                }
            }).filter(option => option.key)
        },
        caseSensitiveDefault(policySchema) {
            const argumentsSchema = this.schemaProperty(policySchema, 'arguments')
            const caseSensitive = this.schemaProperty(argumentsSchema, 'caseSensitive')
            if (Object.prototype.hasOwnProperty.call(caseSensitive, 'const')) {
                return caseSensitive.const
            }
            return Object.prototype.hasOwnProperty.call(caseSensitive, 'default')
                ? caseSensitive.default
                : true
        },
        humanizeKey(key) {
            if (!key) {
                return 'Not configured'
            }

            const segment = key.split('.').pop()
            const words = segment.replace(/_/g, ' ')
            return words.charAt(0).toUpperCase() + words.slice(1)
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-product-behavior__section {
    padding: 1.5rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.manifest-product-behavior__driver-info,
.manifest-product-behavior__policy-description,
.manifest-product-behavior__empty {
    padding: 1rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-product-behavior__strategies {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
}

.manifest-product-behavior__strategy {
    display: flex;
    min-width: 0;
    align-items: center;
    padding: 1rem;
    border: 1px solid #d8d6de;
    border-radius: .357rem;
    color: inherit;
    background: #fff;
    text-align: left;
    transition: border-color .15s ease, background-color .15s ease;
}

.manifest-product-behavior__strategy:not(:disabled):hover,
.manifest-product-behavior__strategy--selected {
    border-color: var(--primary);
    background: rgba(0, 0, 0, .035);
}

.manifest-product-behavior__strategy-marker {
    display: flex;
    flex: 0 0 2.25rem;
    width: 2.25rem;
    height: 2.25rem;
    align-items: center;
    justify-content: center;
    margin-right: .75rem;
    border-radius: 50%;
    color: var(--primary);
    background: rgba(0, 0, 0, .045);
}

.manifest-product-behavior__strategy--selected .manifest-product-behavior__strategy-marker {
    color: #fff;
    background: var(--primary);
}

.manifest-product-behavior__strategy-title {
    display: block;
    margin-bottom: .2rem;
    font-weight: 600;
}

.manifest-product-behavior__strategy code {
    display: block;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.manifest-product-behavior__identity,
.manifest-product-behavior__existing {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #ebe9f1;
}

.manifest-product-behavior__identity--first {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
}

.manifest-product-behavior__fixed-policy {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-radius: .357rem;
    background: rgba(0, 0, 0, .035);
}

.manifest-product-behavior__policy-description {
    margin-top: -.5rem;
    margin-bottom: 1rem;
}

@media (max-width: 767.98px) {
    .manifest-product-behavior__strategies {
        grid-template-columns: 1fr;
    }
}
</style>
